import { useMemo } from 'react';

import { ROUTES } from '@/constants';

import { usePanelIdOrDefault } from './usePanelIdOrDefault';

interface NavItem {
  label: string;
  onClick: () => void;
  isActiveItem: boolean;
  position: number;
}

interface PanelManagementNavigationItemsParams {
  selectedItem: string | null;
  handleNavigation: (href: string, label: string) => void;
  panelId: string | undefined;
  companyId: string | undefined;
  hasPermissionToViewPanel: boolean;
  hasPermissionToPanelistImport: boolean;
  hasPermissionToLeadImport: boolean;
  hasPermissionToManualActivation: boolean;
  hasPermissionToViewMarketingCampaigns: boolean;
  isReferralProgramEnabled: boolean;
  hasPermissionToViewProfiling: boolean;
}

type PermissionKey = Extract<
  keyof PanelManagementNavigationItemsParams,
  `hasPermission${string}` | `is${string}`
>;

interface PanelManagementConfigItem {
  label: string;
  path: string;
  permissionKey: PermissionKey;
}

const PanelManagementConfig: PanelManagementConfigItem[] = [
  {
    label: 'Settings',
    path: '/settings',
    permissionKey: 'hasPermissionToViewPanel',
  },
  {
    label: 'Branding',
    path: '/branding',
    permissionKey: 'hasPermissionToViewPanel',
  },
  { label: 'URL', path: '/url', permissionKey: 'hasPermissionToViewPanel' },
  {
    label: 'Panel redemption methods',
    path: '/panelRedemptionMethods',
    permissionKey: 'hasPermissionToViewPanel',
  },
  {
    label: 'Incentives & margins',
    path: '/dynamicIncentiveSettings/openExchange',
    permissionKey: 'hasPermissionToViewPanel',
  },
  {
    label: 'Panelist import',
    path: '/panelistImport',
    permissionKey: 'hasPermissionToPanelistImport',
  },
  {
    label: 'Lead import',
    path: '/leadImport',
    permissionKey: 'hasPermissionToLeadImport',
  },
  {
    label: 'Manual activation management',
    path: '/manualActivationManagement',
    permissionKey: 'hasPermissionToManualActivation',
  },
  {
    label: 'Marketing campaigns',
    path: '/marketingCampaigns',
    permissionKey: 'hasPermissionToViewMarketingCampaigns',
  },
  {
    label: 'Referral program',
    path: '/referralProgram',
    permissionKey: 'isReferralProgramEnabled',
  },
];

export const panelManagementLabels = PanelManagementConfig.map(
  ({ label }) => label
);

export const usePanelManagementNavigationItems = (
  params: PanelManagementNavigationItemsParams
) => {
  const { selectedItem, handleNavigation, panelId, companyId, ...permissions } =
    params;

  const { panelId: effectivePanelId, companyId: panelDetailCompanyId } =
    usePanelIdOrDefault(panelId, permissions.hasPermissionToViewPanel);

  const effectiveCompanyId = companyId || panelDetailCompanyId;

  const panelManagementItems = useMemo<NavItem[]>(() => {
    if (!permissions.hasPermissionToViewPanel) return [];

    const basePath = effectivePanelId
      ? effectiveCompanyId
        ? `${ROUTES.companies}/${effectiveCompanyId}${ROUTES.panels}/${effectivePanelId}`
        : `${ROUTES.companies}${ROUTES.panels}/${effectivePanelId}`
      : null;

    return PanelManagementConfig.filter(
      ({ permissionKey }) => permissions[permissionKey]
    ).map(({ label, path }, position) => ({
      label,
      onClick: () => {
        if (basePath) handleNavigation(`${basePath}${path}`, label);
      },
      isActiveItem: selectedItem === label,
      position,
    }));
  }, [
    permissions,
    effectivePanelId,
    effectiveCompanyId,
    selectedItem,
    handleNavigation,
  ]);

  return { panelManagementItems };
};
